'use client';

import { useEffect, useState } from 'react';

/**
 * Defers a flag flip until the browser is idle (post-hydration), falling back to
 * a macrotask when `requestIdleCallback` is unavailable. Useful for keeping
 * non-critical client work off the initial render path (e.g. LCP-sensitive pages).
 *
 * Pass `enabled: false` to skip scheduling entirely — no idle callback and no
 * extra re-render when the deferred work is not needed.
 */
export const useIdleFlag = (enabled = true): boolean => {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const win = window as Window & {
      requestIdleCallback?: (callback: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    const handle = win.requestIdleCallback
      ? win.requestIdleCallback(() => setIsIdle(true))
      : win.setTimeout(() => setIsIdle(true), 0);

    return () => {
      if (win.cancelIdleCallback) {
        win.cancelIdleCallback(handle);
      } else {
        win.clearTimeout(handle);
      }
    };
  }, [enabled]);

  return enabled && isIdle;
};
